import api from './api';
import utils from './utils';

const cache = {};
const MOGOPRIORITY = {
    3: 10, // Obtainable for Limited Time
    4: 20, // Bugged
    1: 30, // Unobtainable
    2: 40, // Never Implemented
};
const MOGFPRIORITY = {
    0: 1, // alliance
    1: 1 // horde
};
const MOGSORT = (a, b) => (MOGOPRIORITY[a.unobtainable] || 0) + (MOGFPRIORITY[a.faction] || 0) - (MOGOPRIORITY[b.unobtainable] || 0) - (MOGFPRIORITY[b.faction] || 0);

// helps download our metadata files, and handles any post-processing
// TODO handle rarity merging too?
async function get(field) {
    if (field.startsWith('appearances')) return appearances(field);
    return api.web(field);
}

async function appearances(field) {
    const [ json1, json2, json3 ] = await Promise.all([ api.web('appearancesources'), api.web('appearancesets'), api.web('quests') ]);
    if (!json1?.appearancesources || !json2?.sets || !json3.quests) return null; // failed to load

    let sources = {};
    let visuals = {};
    let quests = {};

    // gather quests by id
    for (let q of json3.quests) {
        quests[q.id] = q.name;
    }

    // gather items by sourceid and visualid
    for (let item of json1.appearancesources) {
        // map blizzard source information
        const source = mapsource(item.source, quests);

        // clone to make sure we don't taint source file
        item = { ...item, ...source, field };

        sources[item.id] = item;

        // TODO should fix any missing visualids
        if (item.visualid) {
            visuals[item.visualid] ??= [];
            visuals[item.visualid].push(item);
        }
    }

    // add set info to sources
    for (let set of json2.sets) {
        for (let id of set.sourceids) {
            if (!sources[id]) continue; // TODO should fix any missing sources

            sources[id].sets ??= [];
            sources[id].sets.push(set);
        }
    }

    if (field === 'appearancesources') {
        // done if we're loading just sources
        return { appearancesources: Object.values(sources) };
    }

    if (field === 'appearances') {
        // group by visualid and return a virtual item
        return { appearances: Object.values(visuals).map(group => {
            // try to pick item that best represents the appearance
            group.sort(MOGSORT);

            // combine sets from any source
            let sets = new Set();
            for (let item of group) {
                if (!item.sets) continue;

                for (let set of item.sets) {
                    sets.add(set);
                }
            }
            sets = sets.size ? Array.from(sets) : undefined;

            return { ...group[0], sets, id: group[0].visualid, shared: group };
        })};
    }

    return null;
}

function mapsource(source, quests) {
    if (!source) return { sourceicon: undefined, source: undefined }; // file not ready yet
    if (!source?.length) return { sourceicon: 'other', source: 'Unknown' };
    source = source[0]; // only return 1 source for now

    if (source.quest) {
        return { sourceicon: 'quest', source: quests[source.quest] || 'Quest' };
    }

    // assume mob drop here
    if (source.name) {
        return { sourceicon: 'drop', source: source.name + ' (' + source.zone + ')' };
    }

    // map blizzard constants
    switch (source.type) {
        case 1: return { sourceicon: 'drop', source: 'Drop' };
        case 2: return { sourceicon: 'quest', source: 'Quest' };
        case 3: return { sourceicon: 'vendor', source: 'Vendor' };
        case 4: return { sourceicon: 'drop', source: 'World Drop' };
        case 7: return { sourceicon: 'achievement', source: 'Achievement' };
        case 8: return { sourceicon: 'profession', source: 'Profession' };
        case 10: return { sourceicon: 'vendor', source: 'Trading Post' };
        default: return { sourceicon: 'other', source: 'Unknown' };
    }
}

async function achievementCategories() {
    if (cache.achievementCategories) return cache.achievementCategories;

    const meta = await get('achievements');
    const set = new Set(meta.achievements.filter(a => a.category).map(a => a.category));

    const categories = Array.from(set);
    categories.sort(utils.collator.compare);

    cache.achievementCategories = categories;
    return categories;
}

async function questZones() {
    if (cache.questZones) return cache.questZones;

    const meta = await get('quests');
    const set = new Set(meta.quests.filter(q => q.category).map(q => q.category));
    set.add('Unknown');

    const zones = Array.from(set);
    zones.sort(utils.collator.compare);

    cache.questZones = zones;
    return zones;
}

const metadata = { get, achievementCategories, questZones };
export default metadata;